/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react/macro";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import classNames from "classnames";
import {
  INITIAL_CATEGORY_ENTITY,
  useForeCategoryEntityQuery,
  useForeChartQuery,
  useForeChartsQuery,
} from "services/api";

import IconBack from "images/icon-back.svg";
import IconHome from "images/icon-home.svg";
import IconInfo from "images/icon-info.svg";
import IconShare from "images/icon-share.svg";
import IconPrint from "images/icon-print.svg";
import IconEye from "images/icon-eye.svg";
import IconA from "images/icon-a.svg";
import IconInfoClose from "images/icon-info-close.svg";

import Card from "../_shared/Card";
import { useSetCategoryContext } from "features/forestage/Categories/CategoryContext";
import { useMediaQuery } from "@mantine/hooks";
import { Helmet } from "react-helmet";
import DangerouslySetHtmlContent from "./DangerouslySetHtmlContent";
import NotFound from "features/forestage/Error";

const Detail = () => {
  const { id } = useParams();
  const { data: chart, isLoading } = useForeChartQuery(id, { skip: !id });

  const { otherCharts } = useForeChartsQuery(chart?.categoryId, {
    skip: !chart || !chart.categoryId,
    selectFromResult: ({ data = [] }) => {
      return { otherCharts: data.filter((x) => x.id !== chart.id) };
    },
  });

  const { data: categoryEntity = INITIAL_CATEGORY_ENTITY } =
    useForeCategoryEntityQuery();

  const [fontSizeAdjust, setFontSizeAdjust] = useState<1 | 1.25 | 1.5>(1);

  const [showMobileDesc, setShowMobileDesc] = useState(false);

  const navigate = useNavigate();

  const setActivatedCategory = useSetCategoryContext();

  useEffect(() => {
    if (!chart || !chart.categoryId) {
      return;
    }

    setActivatedCategory(chart.categoryId);
  }, [chart, setActivatedCategory]);

  const matchesMobile = useMediaQuery("(max-width: 767px)");

  useEffect(() => {
    if (showMobileDesc || matchesMobile) {
      document.body.style.overflow = "hidden";
    }

    if (matchesMobile) {
      window.scrollTo(0, 0);
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showMobileDesc, matchesMobile]);

  if (isLoading) {
    return <div className="h-screen"></div>;
  }

  if (!chart) {
    return <NotFound />;
  }

  const styles = css`
    color: #2a3232 !important;
    font-size: ${16 * fontSizeAdjust}px !important;

    & * {
      color: #2a3232 !important;
      font-size: ${16 * fontSizeAdjust}px !important;
    }
    & h1,
    & h2,
    & h3,
    & h4,
    & h5,
    & h6 {
      color: #000 !important;
    }
    & h1,
    & h1 * {
      font-size: ${30 * fontSizeAdjust}px !important;
    }
    & h2,
    & h2 * {
      font-size: ${28 * fontSizeAdjust}px !important;
    }
    & h3,
    & h3 * {
      font-size: ${26 * fontSizeAdjust}px !important;
    }
    & h4,
    & h4 * {
      font-size: ${22 * fontSizeAdjust}px !important;
    }
    & h5,
    & h5 * {
      font-size: ${18 * fontSizeAdjust}px !important;
    }
    & h6,
    & h6 * {
      font-size: ${16 * fontSizeAdjust}px !important;
    }
    ol,
    ul,
    menu {
      list-style: revert;
      margin-block-start: 1em;
      margin-block-end: 1em;
      margin-inline-start: 0px;
      margin-inline-end: 0px;
      padding-inline-start: 24px;
    }
  `;

  return (
    <section>
      {chart && (
        <Helmet>
          <title>{chart.boardName} - 臺南城市儀表板</title>
          <meta
            property="og:title"
            content={`${chart.boardName} - 臺南城市儀表板`}
          />

          <meta name="description" content={chart.boardDesc} />
          <meta property="og:description" content={chart?.boardDesc} />

          <meta
            property="og:url"
            content={`https://dashboard.tainan.gov.tw/charts/${chart.id}`}
          />
        </Helmet>
      )}

      <div className="-mx-4 -mt-4 flex items-center bg-grandma-300 py-2 px-4 md:mx-0 md:mt-0 md:mb-6 md:bg-transparent md:py-0 md:px-0">
        <button
          className="mr-4 h-12 w-8 shrink-0 rounded-lg bg-white print:hidden md:bg-grandma-300"
          onClick={() => {
            navigate(-1);
          }}
        >
          <img
            className="h-6 w-9 md:brightness-[100]"
            src={IconBack}
            alt="back"
            draggable="false"
          />
        </button>
        <div className="min-w-0 grow">
          <p className="flex items-center text-sm font-normal print:hidden">
            <img src={IconHome} alt="home" draggable="false" />
            &nbsp;{">"}&nbsp;
            {categoryEntity.entities[chart.categoryId]?.name}
          </p>
          <h1 className="overflow-hidden text-ellipsis whitespace-nowrap text-xl font-medium">
            {chart.boardName}
          </h1>
        </div>
        <div className="flex shrink-0 items-center print:hidden">
          <div className="mr-4 hidden items-end md:flex">
            <img
              className={classNames("mr-[0.6rem] w-4 cursor-pointer lg:mr-4", {
                grayscale: fontSizeAdjust !== 1,
              })}
              src={IconA}
              alt="font size"
              draggable="false"
              onClick={() => setFontSizeAdjust(1)}
            />

            <img
              className={classNames("mr-4 w-5 cursor-pointer", {
                grayscale: fontSizeAdjust !== 1.25,
              })}
              src={IconA}
              alt="font size"
              draggable="false"
              onClick={() => setFontSizeAdjust(1.25)}
            />

            <img
              className={classNames("mr-4 w-6 cursor-pointer", {
                grayscale: fontSizeAdjust !== 1.5,
              })}
              src={IconA}
              alt="font size"
              draggable="false"
              onClick={() => setFontSizeAdjust(1.5)}
            />
          </div>

          <img
            className="mr-4 w-6 cursor-pointer md:hidden"
            src={IconInfo}
            alt="info"
            draggable="false"
            onClick={() => setShowMobileDesc(true)}
          />

          <img
            className="mr-1 hidden w-5 md:block"
            src={IconEye}
            alt="views count"
            draggable="false"
          />

          <span className="mr-6 hidden md:block">
            {chart.viewsCount.toLocaleString()}
          </span>

          <img
            className="mr-4 hidden w-6 cursor-pointer md:block"
            src={IconPrint}
            alt="print"
            draggable="false"
            onClick={() => {
              if (window.print) {
                window.print();
              }
            }}
          />

          <img
            className="relative -top-[2px] w-6 cursor-pointer md:brightness-0"
            src={IconShare}
            alt="share"
            draggable="false"
            onClick={() => {
              if (navigator.share && window.location) {
                navigator.share({
                  title: "台南城市儀表板",
                  text: chart.boardName,
                  url: window.location.toString(),
                });
              }
            }}
          />
        </div>
      </div>
      <div className="-mx-4 mb-4 bg-[#C1DBDB] p-2 md:mx-0">
        <DangerouslySetHtmlContent
          className="pt-[calc(100vh-148px)] print:pt-[63%] md:pt-[63%]"
          css={css`
            position: relative;

            & > * {
              position: absolute !important;
              top: 0;
              left: 0;
              width: 100% !important;
              height: 100% !important;
            }
          `}
          html={matchesMobile ? chart.htmlTextMobile : chart.htmlText}
        />
      </div>

      <div className="mb-12 hidden print:block md:block">
        {/* 20111125 隱藏 */}
        {/* <p className="text-[#adadad] print:text-gray-600">
          更新日期：
          {new Date(chart.updateDate).toLocaleString("zh-TW", {
            dateStyle: "long",
          })}
        </p> */}
        <p className="mb-4 text-[#adadad]  print:text-gray-600">
          資料來源：{chart.dataSource || chart.unitName}
          <br />
          備註︰更新時間配合資料來源
        </p>
        <p
          className="mb-12"
          css={styles}
          dangerouslySetInnerHTML={{ __html: chart.boardDesc }}
        ></p>

        {otherCharts.length > 0 && (
          <div className="print:hidden">
            <h3 className="mb-12 flex text-2xl font-normal before:mr-2 before:h-8 before:w-3 before:rounded-sm before:bg-grandma-300 before:content-['']">
              相關儀表板
            </h3>
            <ul className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {otherCharts.map((x) => (
                <li key={x.id}>
                  <Card
                    categoryId={x.categoryId}
                    dataSource={x.dataSource || x.unitName}
                    id={x.id}
                    name={x.boardName}
                    thumbnailUrl={x.thumbnailUrl}
                    url={x.url}
                    viewsCount={x.viewsCount}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {showMobileDesc && (
        <div className="fixed top-0 left-0 z-30 h-[100vh] w-[100vw] bg-grandma-400 bg-opacity-70">
          <div className="w-100 flex h-16 justify-end">
            <img
              className="mr-3 w-9 cursor-pointer"
              src={IconInfoClose}
              alt="close"
              draggable="false"
              onClick={() => {
                setShowMobileDesc(false);
              }}
            />
          </div>
          <div className="w-100 h-[calc(100vh-64px)] overflow-y-scroll">
            <div className="mx-2 mb-4 min-h-[calc(100vh-80px)] rounded-3xl bg-white p-6 pt-8">
              <div className="mb-4 flex items-end">
                <img
                  className={classNames("mr-4 w-4 cursor-pointer", {
                    grayscale: fontSizeAdjust !== 1,
                  })}
                  src={IconA}
                  alt="font size"
                  draggable="false"
                  onClick={() => setFontSizeAdjust(1)}
                />

                <img
                  className={classNames("mr-4 w-5 cursor-pointer", {
                    grayscale: fontSizeAdjust !== 1.25,
                  })}
                  src={IconA}
                  alt="font size"
                  draggable="false"
                  onClick={() => setFontSizeAdjust(1.25)}
                />

                <img
                  className={classNames("mr-4 w-6 cursor-pointer", {
                    grayscale: fontSizeAdjust !== 1.5,
                  })}
                  src={IconA}
                  alt="font size"
                  draggable="false"
                  onClick={() => setFontSizeAdjust(1.5)}
                />
              </div>
              <p className="mb-4 text-xs text-[#adadad]">
                更新日期：
                {new Date(chart.updateDate).toLocaleString("zh-TW", {
                  dateStyle: "long",
                })}
              </p>
              <p
                css={styles}
                dangerouslySetInnerHTML={{ __html: chart.boardDesc }}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
export default Detail;
